// TUT-590 — o estado do chat rápido (as janelas no canto, decisão do dono em
// 02/11): o redutor das janelas e o que sobrevive ao recarregar, puros e sem
// React, para o teste medir sem montar tela.
import { gravarLocal, lerLocal } from "@/lib/armazenamento-local";

import type { RascunhoDaConversa } from "./CompositorDaCaixa";

/** Cabem 3 janelas; a 4ª fecha a mais antiga (que segue na lista). */
export const MAXIMO_DE_JANELAS = 3;

export interface JanelaDoChat {
  /** O id da caixa única: `chat:<uuid>` ou `cw:<número>`. */
  id: string;
  minimizada: boolean;
}

export type AcaoDasJanelas =
  | { tipo: "abrir"; id: string }
  | { tipo: "fechar"; id: string }
  | { tipo: "alternar"; id: string }
  | { tipo: "fecharTodas" };

/** As janelas, da mais antiga para a mais nova. */
export function reduzirJanelas(janelas: JanelaDoChat[], acao: AcaoDasJanelas): JanelaDoChat[] {
  switch (acao.tipo) {
    case "abrir": {
      // Reabrir a que já está aberta só a restaura, sem mudar a ordem.
      if (janelas.some((j) => j.id === acao.id)) {
        return janelas.map((j) => (j.id === acao.id ? { ...j, minimizada: false } : j));
      }
      return [...janelas, { id: acao.id, minimizada: false }].slice(-MAXIMO_DE_JANELAS);
    }
    case "fechar":
      return janelas.filter((j) => j.id !== acao.id);
    case "alternar":
      return janelas.map((j) => (j.id === acao.id ? { ...j, minimizada: !j.minimizada } : j));
    case "fecharTodas":
      // O mesmo array quando já não há janela: o React não re-renderiza.
      return janelas.length ? [] : janelas;
  }
}

/**
 * A chave é versionada e é de cada pessoa em cada escritório: quem entra
 * depois no mesmo navegador não herda as janelas nem o rascunho de ninguém.
 */
export function chaveDoChatRapido(tenantId: string, userId: string): string {
  return `tutelae:chat-rapido:v1:${tenantId}:${userId}`;
}

export interface ChatRapidoGuardado {
  janelas: JanelaDoChat[];
  rascunhos: [string, RascunhoDaConversa][];
}

const VAZIO: ChatRapidoGuardado = { janelas: [], rascunhos: [] };

function ehJanela(valor: unknown): valor is JanelaDoChat {
  const j = valor as JanelaDoChat;
  return typeof j?.id === "string" && typeof j.minimizada === "boolean";
}

function ehRascunho(valor: unknown): valor is [string, RascunhoDaConversa] {
  if (!Array.isArray(valor) || typeof valor[0] !== "string") return false;
  const r = valor[1] as RascunhoDaConversa;
  return typeof r?.texto === "string" && (r.modo === "responder" || r.modo === "nota");
}

/** O que estava guardado; o dado estranho (outra versão, JSON quebrado) vira vazio. */
export function lerChatRapido(chave: string): ChatRapidoGuardado {
  try {
    const dado = JSON.parse(lerLocal(chave) ?? "null") as Partial<ChatRapidoGuardado> | null;
    if (!dado) return VAZIO;
    return {
      janelas: (Array.isArray(dado.janelas) ? dado.janelas.filter(ehJanela) : []).slice(-MAXIMO_DE_JANELAS),
      rascunhos: Array.isArray(dado.rascunhos) ? dado.rascunhos.filter(ehRascunho) : [],
    };
  } catch {
    return VAZIO;
  }
}

/** Grava só o rascunho com texto: o vazio não precisa ser lembrado. */
export function gravarChatRapido(
  chave: string,
  janelas: readonly JanelaDoChat[],
  rascunhos: ReadonlyMap<string, RascunhoDaConversa>,
): void {
  const comTexto = [...rascunhos].filter(([, r]) => r.texto.trim() !== "");
  // ponytail: os rascunhos com texto ficam até serem enviados ou apagados, sem
  // teto; se o armazenamento encher, `gravarLocal` só deixa de lembrar.
  gravarLocal(chave, JSON.stringify({ janelas: [...janelas], rascunhos: comTexto } satisfies ChatRapidoGuardado));
}

/**
 * O `Map` dos rascunhos que avisa a cada gravação: o `CompositorDaCaixa`
 * grava nele a cada tecla (TUT-491), e é por aqui que o rascunho chega ao
 * armazenamento sem o compositor saber.
 */
export class RascunhosQueAvisam extends Map<string, RascunhoDaConversa> {
  aoMudar: (() => void) | null = null;

  override set(chave: string, valor: RascunhoDaConversa): this {
    super.set(chave, valor);
    this.aoMudar?.();
    return this;
  }
}
